import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** Locale used when the caller's tag is rejected by the Intl engine. */
export const FALLBACK_LOCALE_TAG = "en-US";

/**
 * `toLocaleDateString` with a fallback: a malformed tag (the literal string
 * "undefined" once persisted from a `?lng=undefined` link, Sentry
 * NMH-REACT-D9N / OMG-2592) throws a RangeError, and a display helper must
 * never take the gift form down for that. Anything other than a RangeError is
 * still thrown, so a real bug stays visible.
 */
export const safeLocaleDateString = (
  d: Date,
  localeTag: string,
  options: Intl.DateTimeFormatOptions,
) => {
  try {
    return d.toLocaleDateString(localeTag, options);
  } catch (err) {
    if (!(err instanceof RangeError)) throw err;
    return d.toLocaleDateString(FALLBACK_LOCALE_TAG, options);
  }
};

export const formatDateForDatePicker = (
  d: Date | null | undefined,
  // Caller passes the active locale tag + a translated "Today" so the gift
  // date field localizes (OMG-2029). Defaults keep other callers English.
  localeTag = FALLBACK_LOCALE_TAG,
  todayLabel = "Today",
) => {
  if (!d) return undefined;
  const shortDate = safeLocaleDateString(d, localeTag, {
    month: "short",
    day: "numeric",
  });

  const today = new Date();
  if (d.toDateString() === today.toDateString()) {
    return `${todayLabel}, ${shortDate}`;
  } else {
    return `${safeLocaleDateString(d, localeTag, { weekday: "long" })}, ${shortDate}`;
  }
};
